import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arcadeUndergroundTitle, formatAchievementList } from "./gameHubUtils";

const TABS = [
  { id: "dash", label: "Ledger" },
  { id: "games", label: "The floor" },
  { id: "market", label: "Wire" },
  { id: "shop", label: "Fence" },
  { id: "boards", label: "Hall of smoke" },
  { id: "activity", label: "Paper trail" },
];

const GAME_TABS = [
  { id: "coin", label: "Call it" },
  { id: "slots", label: "Slots" },
  { id: "bj", label: "21" },
  { id: "crash", label: "Run" },
];

/** Must match server slot symbol order (economy/pure spinThreeReels). */
const SLOT_SYMBOLS = ["🍒", "🍋", "🍇", "🔔", "⭐", "💎", "7️⃣"];

/** Highlight path that ends on `target` (heads | tails). */
function buildCoinHighlightSequence(target) {
  const other = target === "heads" ? "tails" : "heads";
  const seq = [];
  for (let i = 0; i < 18; i += 1) {
    seq.push(i % 2 === 0 ? "heads" : "tails");
  }
  seq.push(other, target, other, target, target);
  return seq;
}

/** Server enforces the same cap (see economy/constants MAX_BET_ABS). */
const MAX_BET_ABS = 25_000;
const DEFAULT_GAME_BET = 75;

function clampBetAmount(raw) {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(MAX_BET_ABS, n);
}

function BetPresets({ onPick, balance }) {
  if (!balance || balance < 1) return null;
  const presets = [1, 10, 25, 75, 200, 500, 2000, MAX_BET_ABS];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
      {presets.map((x) => {
        const pick = clampBetAmount(Math.min(x, balance));
        const label =
          x === MAX_BET_ABS ? "Max bet" : x === 1 ? "Min 1" : String(x);
        return (
          <button
            key={x}
            type="button"
            onClick={() => onPick(pick)}
            style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, border: "1px solid var(--app-border)", cursor: "pointer" }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function authHeaders(sessionToken) {
  return {
    Authorization: `Bearer ${sessionToken}`,
    "Content-Type": "application/json",
  };
}

function Sparkline({ values, width = 120, height = 36 }) {
  const pts = useMemo(() => {
    if (!values?.length) return "";
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    return values
      .map((v, i) => {
        const x = (i / Math.max(1, values.length - 1)) * width;
        const y = height - ((v - min) / span) * (height - 4) - 2;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }, [values, width, height]);
  if (!pts) return <span style={{ color: "var(--app-muted)", fontSize: 11 }}>—</span>;
  return (
    <svg width={width} height={height} style={{ display: "block" }} aria-hidden>
      <polyline
        fill="none"
        stroke="var(--app-accent)"
        strokeWidth="1.5"
        points={pts}
      />
    </svg>
  );
}

function needsLargeBetConfirm(bet, balance, hint) {
  if (!balance || bet < hint.absolute) return false;
  return bet >= balance * hint.fraction;
}

function makeClientRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function formatSigned(value) {
  if (value == null) return "0";
  return `${value >= 0 ? "+" : ""}${value}`;
}

const cardStyle = {
  border: "1px solid var(--hub-card-border, var(--app-border))",
  borderRadius: 14,
  padding: 14,
  background: "var(--hub-card-bg, var(--app-elevated))",
};

const PODIUM_KEYS = new Set(["richestByNetWorth", "topArcadeXp"]);
const ROMAN_RANK = { 1: "I", 2: "II", 3: "III" };

function HubPodium({ rows, renderSub }) {
  const r = rows || [];
  if (!r.length) return null;
  let slots;
  let places;
  if (r.length >= 3) {
    slots = [r[1], r[0], r[2]];
    places = [2, 1, 3];
  } else if (r.length === 2) {
    slots = [r[1], r[0]];
    places = [2, 1];
  } else {
    slots = [r[0]];
    places = [1];
  }
  return (
    <div className="hub-podium">
      {slots.map((row, i) => (
        <div key={row.userLower} className="hub-podium-slot" data-place={String(places[i])}>
          <div
            className="hub-podium-rank"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: places[i] === 1 ? "#fbbf24" : "rgba(226,232,240,0.65)",
              fontWeight: 700,
            }}
          >
            {ROMAN_RANK[places[i]] || places[i]}
          </div>
          <div className="hub-podium-name">{row.displayName}</div>
          <div className="hub-podium-stat">{renderSub(row)}</div>
        </div>
      ))}
    </div>
  );
}

function RivalHeat({ placement }) {
  if (!placement || (!placement.netWorth && !placement.arcade)) return null;
  const nw = placement.netWorth;
  const ax = placement.arcade;
  return (
    <section className="hub-chase-panel hub-card hub-card-glow" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>Who you&apos;re chasing</h3>
      <p style={{ fontSize: 12, margin: "0 0 12px", color: "var(--app-muted)", lineHeight: 1.45 }}>
        Same boards for the whole crew. Pressure is the point.
      </p>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {nw ? (
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "#fda4af",
                marginBottom: 6,
              }}
            >
              Net worth · #{nw.rank} / {nw.outOf}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Top: <strong>{nw.leader.displayName}</strong> ({nw.leader.netWorth})
            </div>
            {nw.chaseAhead?.length ? (
              <ul className="hub-ol-compact" style={{ marginTop: 8 }}>
                {nw.chaseAhead.map((x) => (
                  <li key={x.displayName}>
                    Overtake <strong>{x.displayName}</strong> — +{x.gap} Coins to tie
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: 13, marginTop: 8 }}>You&apos;re out in front. Everyone else is hunting you.</p>
            )}
          </div>
        ) : null}
        {ax ? (
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "#c4b5fd",
                marginBottom: 6,
              }}
            >
              Arcade rep · #{ax.rank} / {ax.outOf}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              Top: <strong>{ax.leader.displayName}</strong> (Lv {ax.leader.arcadeLevel} · {ax.leader.arcadeXp} XP)
            </div>
            {ax.chaseAhead?.length ? (
              <ul className="hub-ol-compact" style={{ marginTop: 8 }}>
                {ax.chaseAhead.map((x) => (
                  <li key={x.displayName}>
                    Pass <strong>{x.displayName}</strong> — +{x.gap} XP
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: 13, marginTop: 8 }}>You lead the XP board.</p>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function dailyCooldownLabel(profile) {
  if (!profile || profile.canClaimDaily) return "";
  const t = profile.nextDailyClaimAt;
  if (typeof t !== "number") return "Soon.";
  const left = Math.max(0, t - Date.now());
  const h = Math.floor(left / 3600000);
  const m = Math.max(1, Math.ceil((left % 3600000) / 60000));
  if (left <= 0) return "Ready!";
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
}

function LeaderboardSections({ leaderboards }) {
  const lb = leaderboards || {};
  const blocks = [
    {
      title: "Top bags (net worth)",
      key: "richestByNetWorth",
      render: (row) => `${row.netWorth} net · ${row.balance} liquid`,
    },
    {
      title: "Top combined empire",
      key: "topCombinedEmpire",
      render: (row) => `${row.combinedEmpire} total`,
    },
    { title: "Liquid kings", key: "highestBalance", render: (row) => `${row.balance} Coins` },
    { title: "Market sharks", key: "bestTrader", render: (row) => `score ${row.traderScore}` },
    { title: "Heaviest score", key: "biggestSingleWin", render: (row) => `${row.biggestWin} Coins` },
    { title: "Worst beat", key: "biggestSingleLoss", render: (row) => `${row.biggestLoss} Coins` },
    { title: "Volume fiends", key: "mostGamesPlayed", render: (row) => `${row.gamesPlayed} games` },
    {
      title: "Week movers",
      key: "highestWeeklyGain",
      render: (row) => `${row.weekGain >= 0 ? "+" : ""}${row.weekGain}`,
    },
    {
      title: "All-time paper*",
      key: "highestAllTimeGain",
      note: "*Approximate: net worth minus starting Coins and recorded admin adjustments.",
      render: (row) => `${row.allTimeGain >= 0 ? "+" : ""}${row.allTimeGain}`,
    },
    {
      title: "Arcade rep (XP / level)",
      key: "topArcadeXp",
      render: (row) => `Lv ${row.arcadeLevel} · ${row.arcadeXp} XP`,
    },
  ];
  return (
    <>
      {blocks.map(({ title, key, render, note }) => (
        <section key={key} className="hub-card" style={{ padding: "16px 18px" }}>
          <h3 style={{ marginTop: 0 }}>{title}</h3>
          {note ? <p style={{ fontSize: 12, color: "var(--app-muted)" }}>{note}</p> : null}
          {PODIUM_KEYS.has(key) && (lb[key] || []).length ? <HubPodium rows={lb[key]} renderSub={render} /> : null}
          <ol style={{ margin: PODIUM_KEYS.has(key) ? "12px 0 0" : 0, paddingLeft: 20 }}>
            {(lb[key] || []).map((row) => (
              <li key={row.userLower} style={{ marginBottom: 6 }}>
                <strong>{row.displayName}</strong> — {render(row)}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </>
  );
}

export default function EconomyHub({ apiBase, sessionToken, adminToken }) {
  const [tab, setTab] = useState("dash");
  const [gameTab, setGameTab] = useState("coin");
  const [dash, setDash] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState("");

  const [betCoin, setBetCoin] = useState(String(DEFAULT_GAME_BET));
  const [sideCoin, setSideCoin] = useState("heads");
  const [betSlots, setBetSlots] = useState(String(DEFAULT_GAME_BET));
  const [betBj, setBetBj] = useState(String(DEFAULT_GAME_BET));
  const [bjState, setBjState] = useState(null);
  const [betCrash, setBetCrash] = useState(String(DEFAULT_GAME_BET));
  const [crashLive, setCrashLive] = useState(null);
  const crashPollRef = useRef(null);
  const gameTimersRef = useRef([]);
  const [coinFlipUi, setCoinFlipUi] = useState(null);
  const [slotsUi, setSlotsUi] = useState(null);
  const [bjHandGen, setBjHandGen] = useState(0);
  const [dailyPop, setDailyPop] = useState(null);
  const [, setDailyTick] = useState(0);
  const [gameArcadeHint, setGameArcadeHint] = useState(null);
  const gameArcadeTimerRef = useRef(null);

  const [mAsset, setMAsset] = useState("");
  const [mQty, setMQty] = useState(1);
  const [adminUser, setAdminUser] = useState("");
  const [adminDelta, setAdminDelta] = useState(100);
  const [adminReason, setAdminReason] = useState("");
  const [adminEventKey, setAdminEventKey] = useState("tech_boom");
  const [newAsset, setNewAsset] = useState({ id: "", name: "", symbol: "", basePrice: 10 });
  const [adminTxUser, setAdminTxUser] = useState("");

  const refresh = useCallback(async () => {
    if (!sessionToken) return;
    setLoadErr("");
    try {
      const r = await fetch(`${apiBase}/api/economy/dashboard`, { headers: authHeaders(sessionToken) });
      const text = await r.text();
      let j;
      try {
        j = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(text.trim().slice(0, 120) || "Invalid server response");
      }
      if (!r.ok) throw new Error(j.error || "Load failed");
      setDash(j);
    } catch (e) {
      setLoadErr(e.message || "Failed to load economy");
    }
  }, [apiBase, sessionToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (crashPollRef.current) clearInterval(crashPollRef.current);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setDailyTick((t) => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  useEffect(
    () => () => {
      gameTimersRef.current.forEach((x) => clearTimeout(x));
      gameTimersRef.current = [];
    },
    []
  );

  const flashGameArcadeXp = useCallback((j) => {
    const g = j?.arcade?.xpGained;
    if (g > 0) {
      setGameArcadeHint(`+${g} Arcade XP`);
      if (gameArcadeTimerRef.current) window.clearTimeout(gameArcadeTimerRef.current);
      gameArcadeTimerRef.current = window.setTimeout(() => setGameArcadeHint(null), 3200);
    }
  }, []);

  const pushGameTimer = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      gameTimersRef.current = gameTimersRef.current.filter((t) => t !== id);
      fn();
    }, ms);
    gameTimersRef.current.push(id);
    return id;
  }, []);

  const profile = dash?.profile;
  const balance = profile?.balance ?? 0;
  const largeHint = useMemo(() => ({ fraction: 0.45, absolute: 2_000 }), []);
  const portfolio = useMemo(() => {
    const p = dash?.portfolio;
    return Array.isArray(p) ? p : [];
  }, [dash?.portfolio]);
  const selectedAsset = useMemo(
    () => (dash?.market || []).find((asset) => asset.id === mAsset) || null,
    [dash?.market, mAsset]
  );
  const selectedPosition = useMemo(
    () => portfolio.find((position) => position.assetId === mAsset) || null,
    [portfolio, mAsset]
  );
  const orderQty = Number(mQty);
  const marketOrderValue = useMemo(() => {
    if (!selectedAsset || !Number.isFinite(orderQty) || orderQty <= 0) return 0;
    return Math.round(selectedAsset.price * orderQty * 100) / 100;
  }, [orderQty, selectedAsset]);

  const startCrashPolling = useCallback(() => {
    if (crashPollRef.current) clearInterval(crashPollRef.current);
    crashPollRef.current = setInterval(async () => {
      const r = await fetch(`${apiBase}/api/economy/games/crash/state`, {
        headers: authHeaders(sessionToken),
      });
      let s = {};
      try {
        s = JSON.parse(await r.text());
      } catch {
        s = {};
      }
      if (!r.ok) {
        clearInterval(crashPollRef.current);
        crashPollRef.current = null;
        setCrashLive(null);
        await refresh();
        return;
      }
      if (s.ok) {
        setCrashLive(s);
        if (s.bust) {
          clearInterval(crashPollRef.current);
          crashPollRef.current = null;
          await refresh();
        }
      }
    }, 200);
  }, [apiBase, refresh, sessionToken]);

  useEffect(() => {
    const serverBj = dash?.activeGames?.blackjack;
    if (serverBj) {
      setBjState(serverBj);
      return;
    }
    setBjState((prev) => (prev && prev.finished === false ? null : prev));
  }, [dash?.activeGames?.blackjack]);

  useEffect(() => {
    if (dash?.activeGames?.crash && !crashPollRef.current) {
      setCrashLive(dash.activeGames.crash);
      startCrashPolling();
    }
  }, [dash?.activeGames?.crash, startCrashPolling]);

  const runJson = async (path, body, method = "POST", opts = {}) => {
    const { skipRefresh = false } = opts;
    setActionErr("");
    setBusy(true);
    try {
      const requestBody =
        method === "POST"
          ? { ...(body || {}), clientRequestId: body?.clientRequestId || makeClientRequestId() }
          : body;
      const r = await fetch(`${apiBase}${path}`, {
        method,
        headers: authHeaders(sessionToken),
        body: requestBody ? JSON.stringify(requestBody) : undefined,
      });
      const text = await r.text();
      let j;
      try {
        j = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(text.trim().slice(0, 120) || "Invalid server response");
      }
      if (!r.ok) throw new Error(j.error || j.message || "Request failed");
      if (!skipRefresh) await refresh();
      return j;
    } catch (e) {
      setActionErr(e.message || "Error");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const doCoin = async () => {
    const b = clampBetAmount(betCoin);
    if (needsLargeBetConfirm(b, balance, largeHint)) {
      if (!window.confirm(`Bet ${b} Coins? This is a large bet for your balance.`)) return;
    }
    setCoinFlipUi({ phase: "pending", highlight: "heads", choice: sideCoin });
    const j = await runJson(
      "/api/economy/games/coin-flip",
      { bet: b, side: sideCoin },
      "POST",
      { skipRefresh: true }
    );
    if (!j || (j.result !== "heads" && j.result !== "tails")) {
      setCoinFlipUi(null);
      await refresh();
      return;
    }
    const target = j.result;
    const seq = buildCoinHighlightSequence(target);
    let i = 0;
    const step = () => {
      if (i >= seq.length) {
        setCoinFlipUi({
          phase: "done",
          highlight: target,
          result: target,
          choice: sideCoin,
          net: j.net,
          payout: j.payout,
        });
        flashGameArcadeXp(j);
        refresh();
        pushGameTimer(() => setCoinFlipUi(null), 5200);
        return;
      }
      setCoinFlipUi({
        phase: "spin",
        highlight: seq[i],
        choice: sideCoin,
      });
      i += 1;
      const delay = Math.min(55 + i * 5, 195);
      pushGameTimer(step, delay);
    };
    step();
  };

  const doSlots = async () => {
    const b = clampBetAmount(betSlots);
    if (needsLargeBetConfirm(b, balance, largeHint)) {
      if (!window.confirm(`Bet ${b} Coins on slots?`)) return;
    }
    setSlotsUi({ phase: "spin", display: [0, 1, 2] });
    const j = await runJson("/api/economy/games/slots", { bet: b }, "POST", { skipRefresh: true });
    if (!j || !Array.isArray(j.reels) || j.reels.length !== 3) {
      setSlotsUi(null);
      await refresh();
      return;
    }
    const final = j.reels.map((n) => Math.max(0, Math.min(6, Number(n) || 0)));
    const rndSym = () => Math.floor(Math.random() * 7);

    const spinColumn = (colIndex) => {
      let ticks = 0;
      const maxTicks = 14 + colIndex * 7;
      const tick = () => {
        ticks += 1;
        setSlotsUi((prev) => {
          const d = [...(prev?.display || [0, 0, 0])];
          for (let c = 0; c < 3; c += 1) {
            if (c < colIndex) d[c] = final[c];
            else if (c === colIndex) d[c] = ticks >= maxTicks ? final[c] : rndSym();
            else d[c] = rndSym();
          }
          return { phase: "spin", display: d };
        });
        if (ticks < maxTicks) {
          pushGameTimer(tick, 65 + colIndex * 12);
        } else if (colIndex < 2) {
          pushGameTimer(() => spinColumn(colIndex + 1), 120);
        } else {
          setSlotsUi({
            phase: "done",
            display: [...final],
            label: j.label,
            net: j.net,
            payout: j.payout,
          });
          flashGameArcadeXp(j);
          refresh();
          pushGameTimer(() => setSlotsUi(null), 5500);
        }
      };
      tick();
    };
    pushGameTimer(() => spinColumn(0), 80);
  };

  const bjStart = async () => {
    const b = clampBetAmount(betBj);
    if (needsLargeBetConfirm(b, balance, largeHint)) {
      if (!window.confirm(`Bet ${b} Coins on blackjack?`)) return;
    }
    const j = await runJson("/api/economy/games/blackjack/start", { bet: b });
    if (j) {
      if (j.finished) flashGameArcadeXp(j);
      setBjHandGen((g) => g + 1);
      setBjState(j);
    }
  };

  const bjAct = async (action) => {
    const j = await runJson("/api/economy/games/blackjack/action", { action });
    if (j) {
      if (j.finished) flashGameArcadeXp(j);
      setBjHandGen((g) => g + 1);
      setBjState(j);
    }
  };

  const crashStart = async () => {
    const b = clampBetAmount(betCrash);
    if (needsLargeBetConfirm(b, balance, largeHint)) {
      if (!window.confirm(`Bet ${b} Coins on crash?`)) return;
    }
    const j = await runJson("/api/economy/games/crash/bet", { bet: b });
    if (j?.ok) {
      setCrashLive({ multiplier: 1, bust: false });
      startCrashPolling();
    }
  };

  const crashOut = async () => {
    const j = await runJson("/api/economy/games/crash/cashout", {});
    if (crashPollRef.current) clearInterval(crashPollRef.current);
    crashPollRef.current = null;
    setCrashLive(null);
    if (j?.finished) flashGameArcadeXp(j);
    if (j) await refresh();
  };

  const doDaily = async () => {
    const j = await runJson("/api/economy/daily", {}, "POST");
    if (j?.ok) {
      setDailyPop({
        granted: j.granted,
        streak: j.streak,
        lucky: !!j.luckyDouble,
        milestone: j.milestone || null,
        arcadeXp: j.arcade?.xpGained,
      });
      setTimeout(() => setDailyPop(null), 7000);
    }
  };

  const doBuy = () => {
    if (needsLargeBetConfirm(marketOrderValue, balance, largeHint)) {
      if (!window.confirm(`Spend about ${marketOrderValue} Coins on this fake-market order?`)) {
        return null;
      }
    }
    return runJson("/api/economy/market/buy", { assetId: mAsset, qty: Number(mQty) });
  };
  const doSell = () => {
    if (selectedPosition && Number(mQty) >= selectedPosition.qty) {
      if (!window.confirm("Sell your full position in this fake asset?")) {
        return null;
      }
    }
    return runJson("/api/economy/market/sell", { assetId: mAsset, qty: Number(mQty) });
  };

  const cardLabel = (c) => {
    if (!c?.rank) return "?";
    if (c.rank === 1) return "A";
    if (c.rank === 11) return "J";
    if (c.rank === 12) return "Q";
    if (c.rank === 13) return "K";
    return String(c.rank);
  };

  const adminHeaders = () => ({
    Authorization: `Bearer ${adminToken}`,
    "Content-Type": "application/json",
  });

  const adminPost = async (path, body) => {
    if (!adminToken) return;
    setBusy(true);
    setActionErr("");
    try {
      const r = await fetch(`${apiBase}${path}`, {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({ ...(body || {}), clientRequestId: makeClientRequestId() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Admin request failed");
      await refresh();
    } catch (e) {
      setActionErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!sessionToken) {
    return (
      <div className="game-hub-shell" style={{ padding: 24, color: "var(--app-muted)", flex: 1, overflowY: "auto" }}>
        <p style={{ maxWidth: 420, lineHeight: 1.5 }}>
          Sign in to enter the floor. Play money only — no real stakes — but the crew still talks trash from the boards.
        </p>
      </div>
    );
  }

  return (
    <div
      className="game-hub-shell"
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "20px 24px 48px",
        maxWidth: 980,
        margin: "0 auto",
      }}
    >
      <style>
        {`
        @keyframes econ-daily-pop {
          0% { transform: scale(0.96); opacity: 0.85; }
          40% { transform: scale(1.02); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes econ-shine {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
      `}
      </style>
      <header className="hub-hero" style={{ marginBottom: 22 }}>
        <h1 className="hub-hero-title">The pit</h1>
        <p className="hub-hero-tag">
          Off-books games, fake market, real bragging rights. <strong style={{ color: "#fbbf24" }}>Play money only</strong> — no
          cash, no crypto — but losses still sting and wins still flex.
        </p>
        {dash?.hooks?.tagline ? (
          <p style={{ margin: "12px 0 0", fontSize: 12, color: "#a78bfa", fontWeight: 700, letterSpacing: "0.02em" }}>
            {dash.hooks.tagline}
          </p>
        ) : null}
      </header>

      {loadErr ? (
        <p style={{ color: "#f04747" }} role="alert">
          {loadErr}
        </p>
      ) : null}
      {actionErr ? (
        <p style={{ color: "#f04747" }} role="alert">
          {actionErr}
        </p>
      ) : null}

      <nav className="hub-tab-strip" aria-label="Hub sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={tab === t.id ? "hub-tab hub-tab-active" : "hub-tab"}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {!dash ? (
        <p style={{ color: "var(--app-muted)" }}>Loading economy…</p>
      ) : tab === "dash" ? (
        <div style={{ display: "grid", gap: 16 }}>
          <RivalHeat placement={dash.leaderboardPlacement} />

          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 12,
            }}
          >
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Balance</div>
              <div style={bigNum}>{profile.balance} Coins</div>
            </div>
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Net worth</div>
              <div style={bigNum}>{profile.netWorth}</div>
            </div>
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Portfolio</div>
              <div style={bigNum}>{profile.portfolioValue}</div>
            </div>
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Unrealized PnL</div>
              <div style={{ ...bigNum, color: profile.unrealizedPnL >= 0 ? "#34d399" : "#fb7185" }}>
                {profile.unrealizedPnL >= 0 ? "+" : ""}
                {profile.unrealizedPnL}
              </div>
            </div>
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Realized trade PnL</div>
              <div style={{ ...bigNum, color: profile.tradingRealizedPnL >= 0 ? "#34d399" : "#fb7185" }}>
                {formatSigned(profile.tradingRealizedPnL)}
              </div>
            </div>
            <div className="hub-card" style={{ padding: 14 }}>
              <div style={muted}>Weekly gain</div>
              <div style={{ ...bigNum, color: profile.weekGain >= 0 ? "#34d399" : "#fb7185" }}>
                {formatSigned(profile.weekGain)}
              </div>
            </div>
            <div className="hub-card hub-card-glow" style={{ padding: 14 }}>
              <div style={muted}>Combined empire</div>
              <div style={bigNum}>{profile.combinedEmpire ?? profile.netWorth}</div>
            </div>
          </section>

          {profile.arcade ? (
            <section className="hub-card hub-card-glow" style={{ padding: "16px 18px" }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>Street rep · Arcade</h3>
                <span className="hub-rank-pill">{arcadeUndergroundTitle(profile.arcade.level)}</span>
              </div>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--app-muted)", lineHeight: 1.45 }}>
                XP from <strong>Neon row</strong> (intel, reflex, grid duels) and bonus juice from the floor games here. The whole
                crew sees big level-ups in live toasts.
              </p>
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
                Level {profile.arcade.level}
                <span style={{ fontSize: 13, fontWeight: 600, marginLeft: 10, color: "var(--app-muted)" }}>
                  {profile.arcade.totalXp} XP
                </span>
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 999,
                  background: "var(--app-input-bg)",
                  overflow: "hidden",
                  border: "1px solid var(--app-border)",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.min(
                      100,
                      Math.round(
                        (profile.arcade.xpIntoLevel / Math.max(1, profile.arcade.xpForNextLevel)) * 100
                      )
                    )}%`,
                    background: "linear-gradient(90deg, #7c3aed, #fbbf24)",
                    transition: "width 0.35s ease",
                  }}
                />
              </div>
              <div style={{ fontSize: 12, color: "var(--app-muted)", marginTop: 8 }}>
                Grid wins left {profile.arcade.ticWinsRemainingToday} · Reflex tries left {profile.arcade.reactionRemainingToday}
              </div>
              {profile.arcade.achievements?.length ? (
                <div style={{ marginTop: 12, fontSize: 12, lineHeight: 1.5 }}>
                  <span style={{ color: "var(--app-muted)", fontWeight: 700 }}>Notches · </span>
                  {formatAchievementList(profile.arcade.achievements).join(" · ")}
                </div>
              ) : null}
            </section>
          ) : null}

          <section
            className="hub-card"
            style={{
              border: profile.canClaimDaily ? "2px solid rgba(192, 132, 252, 0.55)" : undefined,
              background: profile.canClaimDaily
                ? "linear-gradient(120deg, rgba(88,28,120,0.35), rgba(30,22,48,0.92), rgba(88,28,120,0.25))"
                : undefined,
              backgroundSize: profile.canClaimDaily ? "200% 100%" : undefined,
              animation: profile.canClaimDaily ? "econ-shine 4s ease infinite alternate" : undefined,
              padding: "16px 18px",
            }}
          >
            <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8 }}>
              Daily bag
              {profile.dailyStreak > 0 ? (
                <span style={{ fontSize: 13, fontWeight: 800, color: "#fbbf24", letterSpacing: "0.04em" }}>
                  STREAK ×{profile.dailyStreak}
                </span>
              ) : null}
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--app-muted)", lineHeight: 1.5 }}>
              {profile.canClaimDaily
                ? `Pull builds your streak. Roughly ${profile.dailyGrantMin ?? "—"}–${
                    profile.dailyGrantMax ?? "—"
                  } Coins. Lucky hit (~${profile.dailyLuckyChancePct ?? 6}%) doubles the whole drop.`
                : `Next pull in ${dailyCooldownLabel(profile)}. Ghost the room ~2 days and the streak dies.`}
            </p>
            {dailyPop ? (
              <div
                role="status"
                style={{
                  marginBottom: 12,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(59,165,93,0.18)",
                  border: "1px solid rgba(59,165,93,0.45)",
                  fontSize: 15,
                  fontWeight: 700,
                  animation: "econ-daily-pop 0.55s ease-out",
                }}
              >
                +{dailyPop.granted} Coins
                {dailyPop.lucky ? " — DOUBLE BAG (lucky)" : ""}
                <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4, opacity: 0.95 }}>
                  Streak now ×{dailyPop.streak}
                  {dailyPop.milestone === 7
                    ? " — week streak badge unlocked (keep going!)"
                    : dailyPop.milestone === 14
                      ? " — two-week legend!"
                      : dailyPop.milestone === 30
                        ? " — monthly grinder!"
                        : ""}
                  {dailyPop.arcadeXp ? ` · +${dailyPop.arcadeXp} Arcade XP` : ""}
                </div>
              </div>
            ) : null}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <button
                type="button"
                disabled={busy || !profile.canClaimDaily}
                onClick={doDaily}
                style={{
                  padding: "10px 18px",
                  fontSize: 15,
                  fontWeight: 700,
                  borderRadius: 10,
                  border: "none",
                  cursor: busy || !profile.canClaimDaily ? "not-allowed" : "pointer",
                  background: profile.canClaimDaily ? "var(--app-accent)" : "var(--app-border)",
                  color: profile.canClaimDaily ? "#fff" : "var(--app-muted)",
                }}
              >
                  {profile.canClaimDaily ? "Pull now" : `Wait ${dailyCooldownLabel(profile)}`}
              </button>
              <button type="button" disabled={busy} onClick={refresh}>
                Refresh
              </button>
            </div>
          </section>

          {dash.activeEvents?.length ? (
            <section style={cardStyle}>
              <h3 style={{ marginTop: 0 }}>Active market events</h3>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {dash.activeEvents.map((ev) => (
                  <li key={ev.id}>
                    <strong>{ev.label}</strong> — ends {new Date(ev.endsAt).toLocaleTimeString()}
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <p style={{ color: "var(--app-muted)" }}>No active market events right now.</p>
          )}

          <section className="hub-card" style={{ padding: "16px 18px" }}>
            <h3 style={{ marginTop: 0 }}>Leaderboard snapshot</h3>
            <p style={{ color: "var(--app-muted)", fontSize: 13 }}>Who&apos;s holding the most paper right now</p>
            {(dash.leaderboards?.richestByNetWorth || []).length ? (
              <HubPodium
                rows={(dash.leaderboards?.richestByNetWorth || []).slice(0, 3)}
                renderSub={(row) => `${row.netWorth} net`}
              />
            ) : null}
            <ol style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 13 }}>
              {(dash.leaderboards?.richestByNetWorth || []).slice(0, 5).map((row) => (
                <li key={row.userLower}>
                  {row.displayName} — {row.netWorth} ({row.balance} liquid)
                </li>
              ))}
            </ol>
          </section>

          <section className="hub-card" style={{ padding: "16px 18px" }}>
            <h3 style={{ marginTop: 0 }}>Recent activity</h3>
            {!dash.recentTransactions?.length ? (
              <p style={{ color: "var(--app-muted)" }}>No transactions yet.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", fontSize: 13 }}>
                {dash.recentTransactions.slice(0, 8).map((t) => (
                  <li key={t.id} style={{ borderBottom: "1px solid var(--app-border)", padding: "6px 0" }}>
                    <span style={{ color: "var(--app-muted)" }}>{new Date(t.at).toLocaleString()}</span> — {t.type}{" "}
                    <strong>{t.amount >= 0 ? "+" : ""}{t.amount}</strong> (bal {t.balanceAfter})
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : tab === "games" ? (
        <div>
          <div className="hub-game-tabs">
            {GAME_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setGameTab(t.id)}
                className={gameTab === t.id ? "hub-game-tab hub-game-tab-active" : "hub-game-tab"}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p style={{ color: "var(--app-muted)", fontSize: 13, lineHeight: 1.5, marginBottom: 4 }}>
            Balance: <strong style={{ color: "#fbbf24" }}>{balance}</strong> · Server-side RNG only · Max bet{" "}
            <strong>{MAX_BET_ABS.toLocaleString()}</strong> Coins. The house is hungry; swings are the point.
          </p>
          <div className="hub-risk-grid" aria-label="Risk profile by game">
            <div className="hub-risk-cell">
              <strong>Call it</strong>
              Fair coin, one shot. Low grind, pure nerve on big bets.
            </div>
            <div className="hub-risk-cell">
              <strong>Slots</strong>
              High variance — dry spells and fat hits. Chases the biggest single-screen spike.
            </div>
            <div className="hub-risk-cell">
              <strong>21</strong>
              Decisions matter: hit, stand, double. Slower bleed, more control.
            </div>
            <div className="hub-risk-cell">
              <strong>Run</strong>
              Ride the multiplier — bust is hidden server-side. Greed vs discipline every round.
            </div>
          </div>
          {gameArcadeHint ? (
            <div className="arcade-banner-toast" style={{ marginBottom: 12 }} role="status">
              {gameArcadeHint}
            </div>
          ) : null}

          {gameTab === "coin" ? (
            <div className="hub-card" style={{ padding: "16px 18px" }}>
              <h3 style={{ marginTop: 0 }}>Call it</h3>
              <p style={{ fontSize: 12, color: "var(--app-muted)", marginTop: -4, lineHeight: 1.45 }}>
                Server locks the result before the animation — what you see is what already happened. Big picks still hurt when
                they&apos;re wrong.
              </p>
              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  marginTop: 16,
                  marginBottom: 16,
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "1px solid var(--app-border)",
                  maxWidth: 440,
                }}
              >
                {["heads", "tails"].map((side, idx) => {
                  const lit =
                    coinFlipUi &&
                    (coinFlipUi.phase === "spin" || coinFlipUi.phase === "pending" || coinFlipUi.phase === "done") &&
                    coinFlipUi.highlight === side;
                  const picked = sideCoin === side;
                  const box = (
                    <div
                      key={side}
                      style={{
                        flex: 1,
                        padding: "18px 12px",
                        textAlign: "center",
                        fontWeight: 800,
                        fontSize: 15,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        background: lit
                          ? "linear-gradient(180deg, rgba(88,101,242,0.35), rgba(88,101,242,0.08))"
                          : "var(--app-input-bg)",
                        color: lit ? "var(--app-text)" : "var(--app-muted)",
                        boxShadow: lit ? "inset 0 0 0 2px var(--app-accent), 0 0 24px rgba(88,101,242,0.45)" : "none",
                        transition: "background 0.08s ease, box-shadow 0.08s ease, color 0.08s ease",
                        position: "relative",
                      }}
                    >
                      {side === "heads" ? "Heads" : "Tails"}
                      {picked ? (
                        <div style={{ fontSize: 10, fontWeight: 600, marginTop: 6, opacity: 0.85 }}>Your pick</div>
                      ) : null}
                      {coinFlipUi?.phase === "done" && coinFlipUi.result === side ? (
                        <div
                          className="economy-coin-land"
                          style={{
                            position: "absolute",
                            inset: 4,
                            borderRadius: 8,
                            pointerEvents: "none",
                            border: "2px solid rgba(250, 166, 26, 0.9)",
                          }}
                        />
                      ) : null}
                    </div>
                  );
                  if (idx === 0) {
                    return (
                      <React.Fragment key={side}>
                        {box}
                        <div
                          aria-hidden
                          style={{
                            width: 36,
                            flexShrink: 0,
                            background:
                              "linear-gradient(90deg, var(--app-input-bg), var(--app-border), var(--app-input-bg))",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            className="economy-coin-chaser"
                            style={{
                              position: "absolute",
                              top: "50%",
                              marginTop: -6,
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: "var(--app-accent)",
                              boxShadow: "0 0 14px var(--app-accent), 0 0 28px rgba(88,101,242,0.5)",
                              left: "50%",
                              marginLeft: -6,
                              transform:
                                coinFlipUi?.highlight === "heads"
                                  ? "translateX(-11px)"
                                  : coinFlipUi?.highlight === "tails"
                                    ? "translateX(11px)"
                                    : "translateX(0)",
                              transition: "transform 0.07s linear, opacity 0.07s ease",
                              opacity:
                                coinFlipUi && (coinFlipUi.phase === "spin" || coinFlipUi.phase === "pending")
                                  ? 1
                                  : 0.88,
                            }}
                          />
                        </div>
                      </React.Fragment>
                    );
                  }
                  return box;
                })}
              </div>
              {coinFlipUi?.phase === "done" ? (
                <p style={{ fontSize: 14, marginBottom: 12 }}>
                  Landed on <strong>{coinFlipUi.result}</strong>
                  {coinFlipUi.choice === coinFlipUi.result ? " — you called it!" : " — not this time."} Net{" "}
                  <strong style={{ color: coinFlipUi.net >= 0 ? "#3ba55d" : "#f04747" }}>
                    {formatSigned(coinFlipUi.net)}
                  </strong>
                  {coinFlipUi.payout > 0 ? ` (paid ${coinFlipUi.payout})` : ""}
                </p>
              ) : null}
              <label style={lbl}>
                Bet
                <input
                  type="number"
                  min={1}
                  max={MAX_BET_ABS}
                  value={betCoin}
                  onChange={(e) => setBetCoin(e.target.value)}
                  style={inp}
                />
                <BetPresets balance={balance} onPick={(n) => setBetCoin(String(n))} />
              </label>
              <div style={{ marginTop: 8 }}>
                <label style={{ marginRight: 12 }}>
                  <input type="radio" checked={sideCoin === "heads"} onChange={() => setSideCoin("heads")} /> Heads
                </label>
                <label>
                  <input type="radio" checked={sideCoin === "tails"} onChange={() => setSideCoin("tails")} /> Tails
                </label>
              </div>
              <button type="button" style={{ marginTop: 12 }} disabled={busy || coinFlipUi?.phase === "spin" || coinFlipUi?.phase === "pending"} onClick={doCoin}>
                {coinFlipUi?.phase === "spin" || coinFlipUi?.phase === "pending" ? "Flipping…" : "Flip"}
              </button>
            </div>
          ) : null}

          {gameTab === "slots" ? (
            <div className="hub-card" style={{ padding: "16px 18px" }}>
              <h3 style={{ marginTop: 0 }}>Slots</h3>
              <p style={{ fontSize: 12, color: "var(--app-muted)", marginTop: -4, lineHeight: 1.45 }}>
                Reels resolve on the server — the UI just sells the drama. Dry spells and jackpots are both part of the grind.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  margin: "16px 0",
                  padding: 14,
                  background: "var(--app-input-bg)",
                  borderRadius: 12,
                  border: "1px solid var(--app-border)",
                }}
              >
                {(slotsUi?.display || [0, 1, 2]).map((symIdx, i) => (
                  <div
                    key={i}
                    className={slotsUi?.phase === "spin" ? "economy-slot-reel" : ""}
                    style={{
                      width: 76,
                      height: 92,
                      borderRadius: 10,
                      border: "2px solid var(--app-border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 40,
                      background: "var(--app-elevated)",
                      boxShadow: slotsUi?.phase === "done" ? "0 0 0 2px rgba(88,101,242,0.35)" : undefined,
                    }}
                  >
                    {SLOT_SYMBOLS[symIdx] ?? "?"}
                  </div>
                ))}
              </div>
              {slotsUi?.phase === "done" ? (
                <p style={{ fontSize: 14, marginBottom: 12 }}>
                  <strong>{slotsUi.label}</strong> · Net{" "}
                  <strong style={{ color: slotsUi.net >= 0 ? "#34d399" : "#fb7185" }}>{formatSigned(slotsUi.net)}</strong>
                  {slotsUi.payout > 0 ? ` · won ${slotsUi.payout}` : ""}
                </p>
              ) : null}
              <label style={lbl}>
                Bet
                <input
                  type="number"
                  min={1}
                  max={MAX_BET_ABS}
                  value={betSlots}
                  onChange={(e) => setBetSlots(e.target.value)}
                  style={inp}
                />
                <BetPresets balance={balance} onPick={(n) => setBetSlots(String(n))} />
              </label>
              <button type="button" style={{ marginTop: 12 }} disabled={busy || slotsUi?.phase === "spin"} onClick={doSlots}>
                {slotsUi?.phase === "spin" ? "Spinning…" : "Spin"}
              </button>
            </div>
          ) : null}

          {gameTab === "bj" ? (
            <div className="hub-card" style={{ padding: "16px 18px" }}>
              <h3 style={{ marginTop: 0 }}>Twenty-one</h3>
              <p style={{ fontSize: 12, color: "var(--app-muted)", marginTop: -4, lineHeight: 1.45 }}>
                Push your luck or lock in — doubles cost real stack. Fewer pure coin-flip moments than Call it.
              </p>
              {!bjState || bjState.finished ? (
                <>
                  <label style={lbl}>
                    Bet
                    <input
                      type="number"
                      min={1}
                      max={MAX_BET_ABS}
                      value={betBj}
                      onChange={(e) => setBetBj(e.target.value)}
                      style={inp}
                    />
                    <BetPresets balance={balance} onPick={(n) => setBetBj(String(n))} />
                  </label>
                  <button type="button" style={{ marginTop: 12 }} disabled={busy} onClick={bjStart}>
                    Deal
                  </button>
                  {bjState?.finished ? (
                    <p style={{ marginTop: 12 }}>
                      Net <strong>{bjState.net}</strong> · Payout {bjState.payout}
                    </p>
                  ) : null}
                </>
              ) : (
                <div>
                  <p>
                    Your hand:{" "}
                    {(bjState.player || []).map((c, i) => (
                      <span
                        key={`${bjHandGen}-${i}-${c?.rank}-${c?.suit}`}
                        className="economy-card-pop"
                        style={chip}
                      >
                        {cardLabel(c)}
                      </span>
                    ))}
                  </p>
                  <p>
                    Dealer up:{" "}
                    <span className="economy-card-pop" style={chip}>
                      {cardLabel(bjState.dealerUp)}
                    </span>
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    <button type="button" disabled={busy} onClick={() => bjAct("hit")}>
                      Hit
                    </button>
                    <button type="button" disabled={busy} onClick={() => bjAct("stand")}>
                      Stand
                    </button>
                    {bjState.canDouble ? (
                      <button type="button" disabled={busy} onClick={() => bjAct("double")}>
                        Double
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {gameTab === "crash" ? (
            <div className="hub-card hub-card-glow" style={{ padding: "16px 18px" }}>
              <h3 style={{ marginTop: 0 }}>Run</h3>
              <p style={{ fontSize: 13, color: "var(--app-muted)", lineHeight: 1.45 }}>
                Multiplier climbs until the hidden bust. Cash out early or ride it — you never get a honest clock on when it
                ends. The shimmer bar is vibes only.
              </p>
              {!crashLive ? (
                <>
                  <label style={lbl}>
                    Bet
                    <input
                      type="number"
                      min={1}
                      max={MAX_BET_ABS}
                      value={betCrash}
                      onChange={(e) => setBetCrash(e.target.value)}
                      style={inp}
                    />
                    <BetPresets balance={balance} onPick={(n) => setBetCrash(String(n))} />
                  </label>
                  <button type="button" style={{ marginTop: 12 }} disabled={busy} onClick={crashStart}>
                    Enter run
                  </button>
                </>
              ) : (
                <div>
                  <div
                    className={!crashLive.bust ? "economy-crash-mult" : undefined}
                    style={{ fontSize: 32, fontWeight: 800, margin: "12px 0" }}
                  >
                    {crashLive.bust ? "Busted!" : `${crashLive.multiplier.toFixed(4)}×`}
                  </div>
                  {!crashLive.bust ? (
                    <div
                      className="economy-crash-tension"
                      style={{
                        height: 8,
                        borderRadius: 6,
                        marginBottom: 12,
                        maxWidth: 320,
                        overflow: "hidden",
                      }}
                      title="Decorative only — not tied to when the round ends"
                    />
                  ) : null}
                  {!crashLive.bust ? (
                    <button type="button" disabled={busy} onClick={crashOut}>
                      Cash out
                    </button>
                  ) : (
                    <button type="button" onClick={() => setCrashLive(null)}>
                      New round
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : tab === "market" ? (
        <div style={{ display: "grid", gap: 14 }}>
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Fake market only</h3>
            <p style={{ margin: 0, fontSize: 13, color: "var(--app-muted)" }}>
              These are made-up assets priced only inside this app. No real stocks, no crypto, no cash value.
            </p>
          </section>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}>
            <label style={lbl}>
              Asset
              <select
                value={mAsset}
                onChange={(e) => setMAsset(e.target.value)}
                style={{ ...inp, minWidth: 160 }}
              >
                <option value="">Select…</option>
                {(dash.market || []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.symbol}) @ {a.price}
                  </option>
                ))}
              </select>
            </label>
            <label style={lbl}>
              Qty
              <input type="number" step="0.0001" min="0.0001" value={mQty} onChange={(e) => setMQty(e.target.value)} style={inp} />
            </label>
            <button type="button" disabled={busy || !mAsset} onClick={doBuy}>
              Buy
            </button>
            <button type="button" disabled={busy || !mAsset} onClick={doSell}>
              Sell
            </button>
          </div>
          <p style={{ margin: 0, color: "var(--app-muted)", fontSize: 13 }}>
            Estimated order value: <strong>{marketOrderValue}</strong> Coins
            {selectedPosition
              ? ` | Position ${selectedPosition.qty} @ avg ${selectedPosition.avgCost}`
              : ""}
          </p>
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Your positions</h3>
            {!portfolio.length ? (
              <p style={{ color: "var(--app-muted)", marginBottom: 0 }}>
                No fake assets held yet.
              </p>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>
                {portfolio.map((position) => (
                  <div
                    key={position.assetId}
                    style={{
                      display: "grid",
                      gap: 4,
                      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                      padding: "10px 0",
                      borderBottom: "1px solid var(--app-border)",
                    }}
                  >
                    <div>
                      <strong>{position.symbol}</strong>
                      <div style={muted}>{position.name}</div>
                    </div>
                    <div>
                      <div style={muted}>Qty</div>
                      <div>{position.qty}</div>
                    </div>
                    <div>
                      <div style={muted}>Avg cost</div>
                      <div>{position.avgCost}</div>
                    </div>
                    <div>
                      <div style={muted}>Value</div>
                      <div>{position.marketValue}</div>
                    </div>
                    <div>
                      <div style={muted}>Unrealized PnL</div>
                      <div style={{ color: position.unrealizedPnL >= 0 ? "#3ba55d" : "#f04747" }}>
                        {formatSigned(position.unrealizedPnL)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          {(dash.market || []).map((a) => (
            <div key={a.id} style={{ ...cardStyle, display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
              <div style={{ flex: "1 1 200px" }}>
                <strong>{a.name}</strong>{" "}
                <span style={{ color: "var(--app-muted)" }}>{a.symbol}</span>
                <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{a.price} Coins</div>
                <div style={{ color: a.pctChange >= 0 ? "#3ba55d" : "#f04747" }}>
                  {a.pctChange >= 0 ? "+" : ""}
                  {a.pctChange}%
                </div>
                <div style={{ fontSize: 12, color: "var(--app-muted)" }}>Popularity {a.popularity}</div>
              </div>
              <Sparkline values={a.history} />
            </div>
          ))}
        </div>
      ) : tab === "shop" ? (
        <div style={{ display: "grid", gap: 12 }}>
          <p style={{ color: "var(--app-muted)" }}>Cosmetics only — no gameplay advantage.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            {(dash.shopCatalog || []).map((item) => {
              const owned = profile.inventory?.includes(item.id);
              return (
                <div key={item.id} style={cardStyle}>
                  <div style={{ fontSize: 12, color: "var(--app-muted)" }}>{item.kind}</div>
                  <strong>{item.name}</strong>
                  <p style={{ fontSize: 13, color: "var(--app-muted)" }}>{item.description}</p>
                  <div style={{ fontWeight: 700 }}>{item.price} Coins</div>
                  {owned ? (
                    <span style={{ color: "#3ba55d", fontSize: 13 }}>Owned</span>
                  ) : (
                    <button type="button" disabled={busy} onClick={() => runJson("/api/economy/shop/buy", { itemId: item.id })}>
                      Buy
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Equip</h3>
            <p style={{ fontSize: 13 }}>Owned badges, titles, and themes can be equipped here.</p>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={lbl}>
                Badge
                <select
                  value={profile.equipped?.badgeId || ""}
                  onChange={(e) =>
                    runJson("/api/economy/shop/equip", {
                      badgeId: e.target.value || null,
                    })
                  }
                  style={inp}
                >
                  <option value="">None</option>
                  {(dash.shopCatalog || [])
                    .filter((x) => x.kind === "badge" && profile.inventory?.includes(x.id))
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </label>
              <label style={lbl}>
                Title
                <select
                  value={profile.equipped?.titleId || ""}
                  onChange={(e) =>
                    runJson("/api/economy/shop/equip", {
                      titleId: e.target.value || null,
                    })
                  }
                  style={inp}
                >
                  <option value="">None</option>
                  {(dash.shopCatalog || [])
                    .filter((x) => x.kind === "title" && profile.inventory?.includes(x.id))
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </label>
              <label style={lbl}>
                Theme
                <select
                  value={profile.equipped?.themeId || ""}
                  onChange={(e) =>
                    runJson("/api/economy/shop/equip", {
                      themeId: e.target.value || null,
                    })
                  }
                  style={inp}
                >
                  <option value="">None</option>
                  {(dash.shopCatalog || [])
                    .filter((x) => x.kind === "theme" && profile.inventory?.includes(x.id))
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          </section>
        </div>
      ) : tab === "boards" ? (
        <div style={{ display: "grid", gap: 20 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--app-muted)", lineHeight: 1.5 }}>
            Same numbers for everyone — flex is public. Top bags and arcade rep get podiums; the full list is the receipt.
          </p>
          <LeaderboardSections leaderboards={dash.leaderboards} />
        </div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Transactions</h3>
            {!dash.recentTransactions?.length ? (
              <p style={{ color: "var(--app-muted)" }}>Empty</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
                {dash.recentTransactions.map((t) => (
                  <li key={t.id} style={{ borderBottom: "1px solid var(--app-border)", padding: "8px 0" }}>
                    {t.at} — <strong>{t.type}</strong> {t.amount >= 0 ? "+" : ""}
                    {t.amount} → balance {t.balanceAfter}
                    <pre style={{ fontSize: 11, margin: "4px 0 0", opacity: 0.8 }}>{JSON.stringify(t.meta)}</pre>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>Game history</h3>
            {!dash.recentGames?.length ? (
              <p style={{ color: "var(--app-muted)" }}>Empty</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
                {dash.recentGames.map((g) => (
                  <li key={g.id} style={{ borderBottom: "1px solid var(--app-border)", padding: "8px 0" }}>
                    {g.at} — <strong>{g.game}</strong> bet {g.bet} payout {g.payout} net {g.net}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {adminToken ? (
        <section style={{ ...cardStyle, marginTop: 28, borderColor: "#faa61a" }}>
          <h3 style={{ marginTop: 0 }}>Economy admin (website password)</h3>
          <div style={{ display: "grid", gap: 10, maxWidth: 480 }}>
            <label style={lbl}>
              User (lowercase ok)
              <input value={adminUser} onChange={(e) => setAdminUser(e.target.value)} style={inp} />
            </label>
            <label style={lbl}>
              Coin delta (+/-)
              <input type="number" value={adminDelta} onChange={(e) => setAdminDelta(e.target.value)} style={inp} />
            </label>
            <label style={lbl}>
              Reason
              <input value={adminReason} onChange={(e) => setAdminReason(e.target.value)} style={inp} />
            </label>
            <button type="button" disabled={busy} onClick={() => adminPost("/api/economy/admin/coins", { userName: adminUser.trim(), delta: Number(adminDelta), reason: adminReason })}>
              Apply coins
            </button>
            <button type="button" disabled={busy} onClick={() => adminPost("/api/economy/admin/reset-weekly", {})}>
              Reset weekly leaderboard baselines
            </button>
            <button type="button" disabled={busy} onClick={() => { if (window.confirm("Wipe ALL economy data?")) adminPost("/api/economy/admin/reset", {}); }}>
              Reset entire economy
            </button>
            <label style={lbl}>
              Trigger event
              <select value={adminEventKey} onChange={(e) => setAdminEventKey(e.target.value)} style={inp}>
                {["tech_boom", "meme_season", "server_recession", "whale_buy_in", "panic_sell"].map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" disabled={busy} onClick={() => adminPost("/api/economy/admin/market-event", { eventKey: adminEventKey })}>
              Fire event
            </button>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <input placeholder="asset id" value={newAsset.id} onChange={(e) => setNewAsset((p) => ({ ...p, id: e.target.value }))} style={inp} />
              <input placeholder="name" value={newAsset.name} onChange={(e) => setNewAsset((p) => ({ ...p, name: e.target.value }))} style={inp} />
              <input placeholder="symbol" value={newAsset.symbol} onChange={(e) => setNewAsset((p) => ({ ...p, symbol: e.target.value }))} style={inp} />
              <input placeholder="base price" type="number" value={newAsset.basePrice} onChange={(e) => setNewAsset((p) => ({ ...p, basePrice: e.target.value }))} style={inp} />
            </div>
            <button type="button" disabled={busy} onClick={() => adminPost("/api/economy/admin/asset/add", { ...newAsset, basePrice: Number(newAsset.basePrice) })}>
              Add asset
            </button>
            <button type="button" disabled={busy} onClick={() => adminPost("/api/economy/admin/asset/remove", { assetId: newAsset.id.trim() })}>
              Remove custom asset (by id)
            </button>
            <label style={lbl}>
              Inspect txs (user filter)
              <input value={adminTxUser} onChange={(e) => setAdminTxUser(e.target.value)} style={inp} />
            </label>
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                const q = new URLSearchParams();
                if (adminTxUser.trim()) q.set("user", adminTxUser.trim().toLowerCase());
                q.set("limit", "80");
                const r = await fetch(`${apiBase}/api/economy/admin/transactions?${q}`, { headers: { Authorization: `Bearer ${adminToken}` } });
                const j = await r.json();
                window.alert(JSON.stringify(j.transactions?.slice(0, 5), null, 2) + (j.transactions?.length > 5 ? "\n…" : ""));
              }}
            >
              Preview transactions (alert)
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}

const bigNum = { fontSize: 22, fontWeight: 800, marginTop: 4 };
const muted = { fontSize: 12, color: "var(--app-muted)" };
const lbl = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13 };
const inp = { padding: 8, borderRadius: 6, border: "1px solid var(--app-border)", background: "var(--app-input-bg)", color: "var(--app-text)" };
const chip = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 48,
  marginRight: 6,
  borderRadius: 6,
  background: "var(--app-input-bg)",
  border: "1px solid var(--app-border)",
  fontWeight: 800,
};
